import { useState } from "react";
import { Ellipsis, Pencil, Trash2 } from "lucide-react";

import { buttonVariants } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import ACL_RELATIONSHIPS from "acl-relationships";
import useAuthorized from "hooks/useAuthorized";
import ConfirmDeleteDialog from "components/modals/ConfirmDeleteDialog";
import FormDialog from "components/modals/FormDialog";
import RevenueOpportunityStatusForm, {
  STATUS_DIALOG_CLASS_NAME,
} from "components/forms/RevenueOpportunityStatusForm";
import {
  invalidateRevenueOpportunityStatusQuery,
  useRevenueOpportunityStatusMutations,
} from "hooks/useRevenueOpportunityStatusTable";

export function RevenueOpportunityStatusTableActions({ original }) {
  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);

  const canEditStatus = useAuthorized([
    ACL_RELATIONSHIPS.revenueOpportunity.edit,
  ]);
  const canDeleteStatus = useAuthorized([
    ACL_RELATIONSHIPS.revenueOpportunity.delete,
  ]);

  const { remove } = useRevenueOpportunityStatusMutations({
    onDeleted: () => setDeleteOpen(false),
  });

  // The table flattens the parent/child tree into "Parent (Child)" rows, so a
  // child row edits the parent status form — never a standalone child.
  const editModel = { id: original.parent_id || original.id };

  if (!canEditStatus && !canDeleteStatus) return null;

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger
          aria-label="Revenue Opportunity Status actions"
          className={buttonVariants({ variant: "ghost", size: "icon-sm" })}
        >
          <Ellipsis />
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          {canEditStatus && (
            <DropdownMenuItem onClick={() => setEditOpen(true)}>
              <Pencil />
              Edit
            </DropdownMenuItem>
          )}
          {canDeleteStatus && (
            <DropdownMenuItem
              variant="destructive"
              onClick={() => setDeleteOpen(true)}
            >
              <Trash2 />
              Delete
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
      <FormDialog
        FormComponent={RevenueOpportunityStatusForm}
        model={editModel}
        modelType="Revenue Opportunity Status"
        open={editOpen}
        onOpenChange={setEditOpen}
        contentClassName={STATUS_DIALOG_CLASS_NAME}
        onSuccess={invalidateRevenueOpportunityStatusQuery}
      />
      <ConfirmDeleteDialog
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        title="Delete Revenue Opportunity Status?"
        description="Are you sure you want to delete this Revenue Opportunity Status?"
        onConfirm={() => remove.mutate(original.id)}
      />
    </>
  );
}

export default RevenueOpportunityStatusTableActions;
